import { GitHub } from "react-feather"
import { useTranslation } from "react-i18next"
import { useQuery } from "react-query"
import type { NextPage } from "next"

import type { DatasetInfo } from "~/api/interruptor/datasets"
import { getDatasetsInfo } from "~/api/interruptor/datasets"
import type { DataPage as PrismicDataPage } from "~/api/prismic/types/data-page"
import CustomRichText from "~/components/CustomRichText"
import Link from "~/components/Link"
import { SEO } from "~/components/SEO"
import { Table } from "~/components/Table"
import TailwindProseWrapper from "~/components/TailwindProseWrapper"
import { QueryCacheKeys } from "~/state/ReactQuery"
import * as RichText from "~/utils/prismic/richText"

import { REPO_FOLDER_PATH } from "./utils"

export type DataPageProps = PrismicDataPage["data"] & {
  tableData: DatasetInfo[]
}

export const DataPage: NextPage<DataPageProps> = ({
  title,
  description,
  seo_description,
  seo_image,
  tableData,
}) => {
  const { t } = useTranslation("data")
  const { data = tableData } = useQuery(
    [QueryCacheKeys.DATASETS],
    getDatasetsInfo,
  )

  return (
    <>
      <SEO
        pageTitle={RichText.asText(title)}
        description={seo_description}
        image={seo_image}
      />

      <TailwindProseWrapper>
        <CustomRichText field={title} />
        <CustomRichText field={description} />

        <Table<DatasetInfo>
          columns={[
            {
              key: "name",
              label: t("datasets.table.headers.name"),
            },
            {
              key: "related_pages",
              label: t("datasets.table.headers.relatedPages"),
            },
          ]}
          rows={data}
          getRowKey={(row) => row.github_folder}
          renderers={{
            name: ({ name, github_folder: folder }) => (
              <Link
                className="space-x-2 whitespace-nowrap"
                href={`${REPO_FOLDER_PATH}${folder}`}
                isExternal
              >
                <GitHub className="inline h-4 w-4" aria-hidden />
                <code className="!text-sm">{name}</code>
              </Link>
            ),
            related_pages: ({ related_pages: pages }) => (
              <ul className="!m-0">
                {pages.map(({ title: pageTitle, url }) => (
                  <li key={url} className="!mt-0">
                    <Link href={url}>{pageTitle}</Link>
                  </li>
                ))}
              </ul>
            ),
          }}
        />
      </TailwindProseWrapper>
    </>
  )
}
